import React, { useState, useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';

import ComponentPropTypes from './ComponentPropTypes';
import useBoolean from 'utils/hooks/useBoolean';
import ModelType from '../ModelType';
import _ from 'lodash';
import SimpleTable, { createDefaultActions, SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import EditItemModal from 'components/Modals/EditItemModal';
import { upsert } from 'utils/upsert';
import { defaultColors } from 'utils/colors';
import { PlotlineTypes } from 'utils/types';
import { LineStyles } from 'utils/types';


const valueOptions = [
  { value: PlotlineTypes.StaticValue, text: 'Static Value' },
  { value: PlotlineTypes.ReportedProperty, text: 'Reported Property' },
  { value: PlotlineTypes.DesiredProperty, text: 'Desired Property' }
];

const lineStyleOptions = [
  { value: LineStyles.Solid, text: 'Solid' },
  { value: LineStyles.Dash, text: 'Dash' },
  { value: LineStyles.Dash, text: 'Dot' },
  { value: LineStyles.DashDot, text: 'Dash Dot' },
  { value: LineStyles.ShortDash, text: 'Short Dash' },
  { value: LineStyles.ShortDashDot, text: 'Short Dash Dot' },
  { value: LineStyles.LongDashDot, text: 'Long Dash Dot' }
];

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};

const defaultValue = {
  plotType: PlotlineTypes.ReportedProperty,
  color: defaultColors[0],
  lineStyle: LineStyles.Solid,
  width: 1,
  displayName: '',
  noDiplaysName: false
};
export const PlotlinesComponent = ({ model, value, onChange }) => {
  const [isLineEditorOpen, openLineEditor, closeLineEditor] = useBoolean(false);
  const [lineData, setLineData] = useState(!_.isEmpty(value) ? _.cloneDeep(value) : []);
  const [selectedLine, setSelectedLine] = useState(null);
  const T = useTranslation();
  const editModel = useMemo(() => {
    const valueType = (selectedLine?.plotType !== PlotlineTypes.StaticValue) ?
      [{
        path: 'propertyValue',
        label: T.common.propertyValue,
        type: ModelType.PROPERTIES
      }] :
      [
        {
          path: 'staticValue',
          label: T.common.staticValue,
          type: ModelType.TEXT
        },
        {
          path: 'displayName',
          label: T.common.displayname,
          type: ModelType.TEXT
        }
      ];
    return [
      {
        path: 'plotType',
        label: T.common.type,
        type: ModelType.OPTIONSV2,
        options: valueOptions
      },
      ...valueType,
      {
        path: 'noDisplayName',
        label: 'No Display Name',
        type: ModelType.SWITCH,
        onlySwitch: true,
        isReversed: false
      },
      {
        path: 'color',
        label: T.common.color,
        type: ModelType.COLOR_PICKER
      },
      {
        path: 'lineStyle',
        label: T.common.lineStyle,
        type: ModelType.OPTIONSV2,
        options: lineStyleOptions
      },
      {
        path: 'width',
        label: T.common.width,
        type: ModelType.NUMBER_INPUT
      }
    ];
  }, [T.common.color, T.common.displayname, T.common.lineStyle, T.common.propertyValue, T.common.staticValue, T.common.type, T.common.width, selectedLine?.plotType]);


  const columns = [

    {
      path: 'plotType',
      label: T.common.type
    },
    {
      path: '.',
      label: T.common.value,
      render: row => {
        switch (row.plotType) {
          case PlotlineTypes.StaticValue:
            return row.staticValue;
          case PlotlineTypes.ReportedProperty:
          case PlotlineTypes.DesiredProperty:
            return row.propertyValue?.[0].displayName;
          default:
            return '';
        }
      }
    },
    {
      path: 'color',
      label: T.common.color,
      render: color => <div style={{ backgroundColor: color, width: '30px', height: '20px' }} />
    },
    {
      path: 'lineStyle',
      label: T.common.lineStyle
    }
  ];

  const newLine = useCallback(() => {
    setSelectedLine({ id: nanoid(), ...defaultValue });
    openLineEditor();
  }, [openLineEditor]);

  const onRowClick = useCallback(row => {
    setSelectedLine(row);
    openLineEditor();
  }, [openLineEditor]);

  const onDelete = useCallback(row => {
    setLineData(oldLineData => {
      _.remove(oldLineData, { id: row.id });
      onChange(model, _.cloneDeep(oldLineData));
      return [...oldLineData];
    });
  }, [model, onChange]);

  const _onSaveLine = useCallback(() => {
    setLineData(oldLineData => {
      upsert(oldLineData, selectedLine, { id: selectedLine.id });
      onChange(model, _.cloneDeep(oldLineData));
      return _.cloneDeep(oldLineData);
    });
    closeLineEditor();
  }, [closeLineEditor, model, onChange, selectedLine]);

  const _onCancelLine = useCallback(() => {
    closeLineEditor();
  }, [closeLineEditor]);


  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);

  return (
    <>
      <SimpleTable
        columns={columns}
        actions={actions}
        data={lineData}
        globalActions={[
          { onClick: newLine }
        ]}
        onRowClick={onRowClick}
        sortBy={DefaultSortBy}
        compact
      />
      <EditItemModal
        isOpen={isLineEditorOpen}
        title={T.modals.selectsignals.title}
        item={selectedLine}
        setItem={setSelectedLine}
        onSave={_onSaveLine}
        models={editModel}
        onCancel={_onCancelLine}
        help="panels/common/plotlines/edit.md"
      />
    </>


  );
};


PlotlinesComponent.propTypes = ComponentPropTypes;

export default React.memo(PlotlinesComponent);
